import {
	Box,
	IconButton,
	Input,
	Text,
	Accordion,
	AccordionItem,
	AccordionButton,
	AccordionIcon,
	AccordionPanel,
	Badge,
	Flex,
	Stack,
} from "@chakra-ui/react";
import { useContext, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { apiRequest } from "utils/api";
import { AlertsContext } from "providers/alerts";
import { getErrorMessage } from "utils/errorHandler";
import { SectionDeleteButton } from "./section-delete-button";
import { FieldViewModel } from "../fields/field.view-model";
import { SectionViewModel } from "./section.view-model";
import { Fields } from "../fields/fields";

interface SectionProps {
	section: SectionViewModel;
	onDelete: (section: SectionViewModel) => void;
	onUpdate: (section: SectionViewModel) => void;
	onAdd: (section: SectionViewModel) => void;
}

export function Section({ section, onDelete, onUpdate, onAdd }: SectionProps) {
	const [editing, setEditing] = useState(!section?.id);

	const { showSuccess, showError } = useContext(AlertsContext);

	const [newSectionName, setNewSectionName] = useState(section?.section_name);

	const [isSavingSection, setIsSavingSection] = useState(false);

	function hasFieldsForApplication() {
		return section.fields?.some(
			(field: any) =>
				field.show_on_lender_application || field.show_on_borrower_application
		);
	}

	function hasFieldsForLoan() {
		return section.fields?.some(
			(field: any) => field.show_on_lender_loan || field.show_on_borrower_loan
		);
	}

	function handleEditClick() {
		setEditing(true);
	}

	async function handleSaveChanges() {
		if (!newSectionName) {
			showError({
				title: "Section Name is required",
				description: "Please enter a name for the section",
			});
			return;
		}

		try {
			setIsSavingSection(true);

			if (!section?.id) {
				const { loan_section } = await apiRequest<{
					loan_section: SectionViewModel;
				}>("/supabase/loan_section/insert", {
					name: newSectionName,
				});

				showSuccess({
					title: "Section Created",
					description: "Section was successfully created",
				});

				loan_section.fields = [];

				onAdd(loan_section);
			} else {
				await apiRequest("/supabase/loan_section/update", {
					id: section.id,
					name: newSectionName,
				});

				showSuccess({
					title: "Section Updated",
					description: "Section was successfully updated",
				});

				onUpdate(section);
			}

			setEditing(false);
		} catch (err) {
			showError({
				title: "Could Not Load Loan Sections",
				description: getErrorMessage(err),
			});
		} finally {
			setIsSavingSection(false);
		}
	}

	function handleFieldsReordered(newOrder: FieldViewModel[]) {
		section.fields = newOrder;
		onUpdate(section);
	}

	function handleHideFromBorrowers(changedFields: FieldViewModel[]) {
		section.fields = changedFields;
		onUpdate(section);
	}

	function handlerEditSectionCancel() {
		setEditing(false);
		setNewSectionName(section.section_name);

		if (!section?.id) {
			onDelete(section);
		}
	}

	function handleOnUpdateField(field: FieldViewModel) {
		section.fields = section.fields.map((f) => (f.id === field.id ? field : f));
		onUpdate(section);
	}

	function handleOnDeleteField(field: FieldViewModel) {
		section.fields = section.fields.filter((f) => f.id !== field.id);
		onUpdate(section);
	}

	function handleOnAddField(field: FieldViewModel) {
		section.fields = [...section.fields, field];
		onUpdate(section);
	}

	return (
		<>
			<Flex>
				<Box flex="1">
					<Accordion allowToggle flex="1">
						<AccordionItem
							key={section.id}
							borderColor="gray.50"
							borderTopWidth="0"
						>
							<Flex
								alignItems="center"
								_hover={{
									backgroundColor: "gray.25",
								}}
							>
								{/* ACTIONS */}
								<Box>
									<Flex>
										{editing ? (
											/* SAVE AND CANCEL BUTTONS */
											<>
												{/* SAVE BUTTON */}
												<Box>
													<IconButton
														onClick={handleSaveChanges}
														aria-label="Save Changes"
														variant="outline"
														size="sm"
														color="#9E9AA3"
														borderColor="#9E9AA3"
														mr="2"
														_hover={{
															color: "black",
															borderColor: "black",
														}}
														icon={<FontAwesomeIcon icon={["far", "check"]} />}
														isLoading={isSavingSection}
													/>
												</Box>

												{/* CANCEL BUTTON */}
												<Box float="left">
													<IconButton
														onClick={handlerEditSectionCancel}
														aria-label="Cancel Changes"
														variant="outline"
														size="sm"
														color="#9E9AA3"
														borderColor="#9E9AA3"
														_hover={{
															color: "black",
															borderColor: "black",
														}}
														icon={<FontAwesomeIcon icon={["far", "xmark"]} />}
														isDisabled={isSavingSection}
													/>
												</Box>
											</>
										) : (
											/* EDIT BUTTON */
											<Box>
												<IconButton
													onClick={handleEditClick}
													aria-label="edit-field"
													variant="outline"
													size="sm"
													borderColor="#9E9AA3"
													color="#9E9AA3"
													_hover={{
														color: "black",
														borderColor: "black",
													}}
													icon={<FontAwesomeIcon icon={["far", "pencil"]} />}
												/>
											</Box>
										)}
									</Flex>
								</Box>

								{/* SECTION NAME */}
								<Box flex="1">
									{editing ? (
										<Box p="0.5rem">
											<Input
												type="text"
												variant="outline"
												value={newSectionName}
												onChange={(e) => setNewSectionName(e.target.value)}
												placeholder="Section Name"
											/>
										</Box>
									) : (
										<AccordionButton
											py={4}
											_hover={{
												backgroundColor: "none",
											}}
										>
											<Text
												as="span"
												flex="1"
												textAlign="left"
												fontWeight="semibold"
												color="black"
											>
												{newSectionName}
											</Text>

											<Stack direction="row" mr="4">
												<Badge
													variant="yellow"
													display={hasFieldsForApplication() ? "block" : "none"}
												>
													Application
												</Badge>

												<Badge
													variant="lightBlue"
													display={hasFieldsForLoan() ? "block" : "none"}
												>
													Loan
												</Badge>
											</Stack>

											<AccordionIcon />
										</AccordionButton>
									)}
								</Box>
							</Flex>

							{/* SECTION FIELDS */}
							<AccordionPanel pt={0}>
								<Fields
									fields={section.fields}
									section={section}
									loan_type={null}
									onFieldUpdated={handleOnUpdateField}
									onFieldDeleted={handleOnDeleteField}
									onFieldAdded={handleOnAddField}
									onFieldsReordered={handleFieldsReordered}
									onHideFromBorrowers={handleHideFromBorrowers}
								/>

								<SectionDeleteButton section={section} onDelete={onDelete} />
							</AccordionPanel>
						</AccordionItem>
					</Accordion>
				</Box>
			</Flex>
		</>
	);
}
